import { ArrowSquareOut } from "@phosphor-icons/react";
import classNames from "classnames";
import * as React from "react";
import styles from "./Navbar.module.css";

export interface DropdownItem {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  href: string;
  external?: boolean;
  badge?: string;
  disabled?: boolean;
}

export interface DropdownSection {
  title: string;
  items: DropdownItem[];
  layout?: "column" | "row";
}

export interface DropdownProps {
  sections?: DropdownSection[];
  featured?: React.ReactNode;
  featuredOnRight?: boolean;
  bottomBar?: React.ReactNode;
  children?: React.ReactNode;
  width?: "auto" | "narrow" | "medium" | "wide";
  className?: string;
  isSwitching?: boolean;
  isExiting?: boolean;
}

const ExternalLinkIcon = () => <ArrowSquareOut size={12} weight="bold" />;

export const Dropdown: React.FC<DropdownProps> = ({
  sections,
  featured,
  featuredOnRight = false,
  bottomBar,
  children,
  width = "wide",
  className,
  isSwitching = false,
  isExiting = false,
}) => {
  const dropdownClassName = classNames(
    styles.dropdown,
    styles[`dropdown${width.charAt(0).toUpperCase() + width.slice(1)}`],
    className,
  );

  const titleClassName = classNames(
    styles.dropdownSectionTitle,
    isExiting && styles.animateExit,
    isSwitching && !isExiting && styles.animateSwitching,
    !isSwitching && !isExiting && styles.animateEnter,
  );

  const itemClassName = classNames(
    styles.dropdownItem,
    isExiting && styles.animateExit,
    isSwitching && !isExiting && styles.animateSwitching,
    !isSwitching && !isExiting && styles.animateEnter,
  );

  const featuredClassName = classNames(
    styles.dropdownFeatured,
    isExiting && styles.animateExit,
    isSwitching && !isExiting && styles.animateSwitchingDelayed,
    !isSwitching && !isExiting && styles.animateEnterDelayed,
  );

  const bottomBarClassName = classNames(
    styles.dropdownBottomBar,
    isExiting && styles.animateExit,
    isSwitching && !isExiting && styles.animateSwitchingDelayed,
    !isSwitching && !isExiting && styles.animateEnterDelayed,
  );

  // If children are provided, use them directly for full customization
  if (children) {
    return (
      <div className={dropdownClassName}>
        {children}
        {bottomBar && <div className={bottomBarClassName}>{bottomBar}</div>}
      </div>
    );
  }

  // Otherwise use the structured layout
  return (
    <div className={dropdownClassName}>
      <div className={styles.dropdownContent}>
        {!featuredOnRight && featured && (
          <div className={featuredClassName}>{featured}</div>
        )}
        {sections && sections.length > 0 && (
          <div className={styles.dropdownSections}>
            {sections.map((section) => (
              <div key={section.title} className={styles.dropdownSection}>
                <span className={titleClassName}>{section.title}</span>
                <div
                  className={
                    section.layout === "row"
                      ? styles.dropdownItemsRow
                      : styles.dropdownItems
                  }
                >
                  {section.items.map((item) => {
                    const itemClasses = classNames(
                      itemClassName,
                      item.disabled && styles.dropdownItemDisabled,
                    );

                    const content = (
                      <>
                        {item.icon && (
                          <span className={styles.dropdownItemIcon}>
                            {item.icon}
                          </span>
                        )}
                        <div className={styles.dropdownItemText}>
                          <span className={styles.dropdownItemTitle}>
                            {item.title}
                            {item.badge && (
                              <span className={styles.dropdownItemBadge}>
                                {item.badge}
                              </span>
                            )}
                            {item.external && (
                              <span className={styles.externalIcon}>
                                <ExternalLinkIcon />
                              </span>
                            )}
                          </span>
                          {item.description && (
                            <span className={styles.dropdownItemDescription}>
                              {item.description}
                            </span>
                          )}
                        </div>
                      </>
                    );

                    if (item.disabled) {
                      return (
                        <div key={item.title} className={itemClasses}>
                          {content}
                        </div>
                      );
                    }

                    return (
                      <a
                        key={item.title}
                        href={item.href}
                        className={itemClasses}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                      >
                        {content}
                      </a>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {featuredOnRight && featured && (
          <div className={featuredClassName}>{featured}</div>
        )}
      </div>
      {bottomBar && <div className={bottomBarClassName}>{bottomBar}</div>}
    </div>
  );
};
